import videojs from 'video.js';
import { v4 as uuid } from 'uuid';
import { multistreamMiddleware } from './multistream/middleware.js';
import StreamPlayer from './multistream/stream-player.js';

import './multistream.css';

const _container = new WeakMap();
const _streamPlayers = new WeakMap();
const _currentTimeWhenBufferingStarted = new WeakMap();
const _firstPlay = new WeakMap();
const _compositeAspect = new WeakMap();
const _compositionList = new WeakMap();
const _currentCompositionIndex = new WeakMap();
const _primarySourceId = new WeakMap();

/**
 * The Multistream module.<br><br>
 * Note: this module is optional and might not be available on every MediasitePlayer instance.
 * @member {MultistreamPlugin|undefined} multistream
 * @memberof MediasitePlayer#
 */

/**
 * Provides support for playing back legacy presentations with multiple synchronized video & slide streams that haven't been converted to the new composited form yet.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class MultistreamPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);
        this.log('Loaded multistream plugin.');

        // Use custom middleware to intercept play() calls triggered through a user interaction event listener (needed for iOS Safari playback)
        videojs.use('*', multistreamMiddleware);
    }

    /**
     * Initializes the Multistream plugin.
     */
    init() {
        const player = this.player;

        // Listen for events from the main player
        this.on(player, 'seeking', this.#handlePlayerSeeking);
        this.on(player, 'timeupdate', this.#handlePlayerTimeupdate);
        this.on(player, 'pause', this.#handlePlayerPause);
        this.on(player, 'ratechange', this.#handlePlayerRatechange);

        _streamPlayers.set(this, []);
        _currentTimeWhenBufferingStarted.set(this, 0);
        _firstPlay.set(this, true);
        _currentCompositionIndex.set(this, 0);

        // Create a container and move the main video element into it
        const Component = videojs.getComponent('component');
        const container = new Component(player);
        container.addClass('mediasite-player__multistream-container');
        // BAD: This plugin should not be using getChild to get a something that belongs to another plugin.
        const smartZoomIndex = this.player.layout().children().indexOf(this.player.getChild('SmartZoom'));
        player.layout().addChild(container, null, smartZoomIndex);
        _container.set(this, container);

        container.addChild(player.tech(true));

        // Set smartzoom to target the multistream container instead of the main video element and set a default aspect ratio
        _compositeAspect.set(this, 1);
        player.smartzoom().aspect = _compositeAspect.get(this);
        player.smartzoom().target = container.el();

        //TODO: Reexamine expected behavior for multistream picture in picture
        //For now, disable Picture in Picture for multistream presentations
        player.settings().enablePIP = false;
    }

    /**
     * Sets the color of the canvas behind the mult-streams.
     * @param {color} color A valid CSS value for the background color 
     */
    setBackgroundColor(color) {
        const container = _container.get(this);
        container.el().style.setProperty('--multistream-background-color', color);
    }

    /**
     * Sets the image url of the canvas behind the mult-streams.
     * @param {string} url A valid image url to be rendered as the background
     */
    setBackgroundImage(url) {
        const container = _container.get(this);
        container.el().style.setProperty('--multistream-background-url', `url('${url}')`);
    }

    /**
     * Sets the size and position of the main stream within the composition.
     * @param {number} x A number between 0.0 and 1.0.
     * @param {number} y A number between 0.0 and 1.0.
     * @param {number} width A number between 0.0 and 1.0.
     * @param {number} height A number between 0.0 and 1.0.
     */
    setMainStreamSize(x, y, width, height) {
        const element = this.player.tech(true).el();
        this.setElementSize(element, x, y, width, height);

        this.#addSmartZoomRegion('multistream-main', {
            x: x,
            y: y,
            width,
            height,
         });
    }

    /**
     * Sets the size and position of a stream element given the rect param.
     * @param {HTMLElement} element The element to set the size of.
     * @param {object} rect A rect object with x, y, width, and height properties.
     */
    setStreamElementSize(element, rect) {
        this.setElementSize(element, rect.left, rect.top, rect.width, rect.height);
    }

    /**
     * Sets the size and position of a stream element given x, y, width, and height.
     * @param {HTMLElement} element The element to set the size of.
     * @param {number} x A number between 0.0 and 1.0.
     * @param {number} y A number between 0.0 and 1.0.
     * @param {number} width A number between 0.0 and 1.0.
     * @param {number} height A number between 0.0 and 1.0.
     */
    setElementSize(element, x, y, width, height) {
        // Main stream with zero height is likely an audio-only stream, but also the source of audio for the presentation.
        // Adjust the element height to be non-zero.
        const fudgedHeight = (height === 0) ? 0.01 : height;

        element.style.left = `${x * 100}%`;
        element.style.top = `${y * 100}%`;
        element.style.width = `${width * 100}%`;
        element.style.height = `${fudgedHeight * 100}%`;
    }

    /**
     * The aspect ratio of the composition.
     * @type {number}
     */
    get aspect() {
        return _compositeAspect.get(this);
    }

    set aspect(aspect) {
        _compositeAspect.set(this, aspect);
        this.player.smartzoom().aspect = aspect;
    }

    /**
     * The list of possible composition maps.
     * @type {Array}
     */
    get compositionList() {
        return _compositionList.get(this);
    }

    set compositionList(list) {
        _compositionList.set(this, list);
    }

    /**
     * The SourceId for the primary stream.
     * @type {string}
     */
    get primarySourceId() {
        return _primarySourceId.get(this);
    }

    set primarySourceId(id) {
        _primarySourceId.set(this, id);
    }

    /**
     * An index that determines which composition map to use.
     * @type {number}
     */
    get currentCompositionIndex() {
        return _currentCompositionIndex.get(this);
    }

    /**
     * Increment currentCompositionIndex or return to 0
     */
    incrementCompositionIndex() {
        const newIndex = (this.currentCompositionIndex + 1) % this.compositionList.length;
        _currentCompositionIndex.set(this, newIndex);
    }

    /**
     * Adds a stream to the composition.
     * @param {import('../../api/presentation.js').MediaStream} stream
     */
    add(stream) {
        return new Promise((resolve, reject) => {
            const id = uuid();
            const container = _container.get(this);
            const streamPlayers = _streamPlayers.get(this);

            //TODO: Remove once Chromium bug has been fixed - https://issues.chromium.org/issues/353552554
            //In Chromium, if multiple videos have same dimensions then sometimes hovering over them causes flickering
            //between the two videos to occur.  In the case of balanced streams we will make one stream .1% smaller to avoid bug.
            //Always make the right stream the slightly smaller stream so the streams still always line up in the center and to avoid
            //situations where both streams are set smaller
            if (stream.width === 0.5 && stream.height === 0.5 && stream.x === 0.5) {
                stream.width = 0.499;
                stream.height = 0.499;
            }

            const playerComponent = new StreamPlayer(this.player, { stream });
            playerComponent.addClass('mediasite-player__multistream-stream-player');
            container.addChild(playerComponent);
            playerComponent.initStream();
            playerComponent.priority = stream.priority;

            _streamPlayers.set(this, [...streamPlayers, { id, waiting: false, streamPlayer: playerComponent }]);

            this.#addSmartZoomRegion(id, {
                x: stream.x,
                y: stream.y,
                width: stream.width,
                height: stream.height
            });
            
            playerComponent.one('loadeddata', () => {
                this.syncAllPlayers().then(resolve);
            });
        });
    }

    #addSmartZoomRegion(id, rect) {
        if (rect.width <= 0 || rect.height <= 0) {
            // A region with zero area makes exactly zero sense. Let's get out of here.
            return;
        }

        this.player.smartzoom().addRegion({
            left: rect.x,
            top: rect.y,
            width: rect.width,
            height: rect.height,
            id
        });
    }

    /**
     * Return the array of stream players.
     */
    getAllStreams() {
        return _streamPlayers.get(this);
    }

    getSlideStream() {
        const streams = _streamPlayers.get(this);
        return streams?.find(stream => {
            return stream.streamPlayer.stream.streamType === 2;
        })?.streamPlayer
    }

    remove(src) {
        // Todo: enable removing a multistream player
        // Find the player with this src
        // Remove it from the DOM
        // Dispose of it
    }

    /**
     * Synchronizes all secondary players to the main stream.
     * @returns {Promise} A promise that is resolved when all streams have finished seeking.
     */
    syncAllPlayers() {
        const existingPlayers = _streamPlayers.get(this);
        const seekPromises = [];
        const mainTime = this.player.currentTime();
        const mainPlaybackRate = this.player.playbackRate();

        for (const { streamPlayer, waiting } of existingPlayers) {
            seekPromises.push(new Promise((resolve, reject) => {
                streamPlayer.any(['seeked', 'playing', 'timeupdate'], resolve);
            }));
            streamPlayer.currentTime = mainTime;
            streamPlayer.playbackRate = mainPlaybackRate;
        }

        return Promise.all(seekPromises);
    }

    /**
     * A boolean indicating if there are any streams in the composition that are currently waiting.
     * @type {boolean}
     */
    get waiting() {
        const streamPlayers = _streamPlayers.get(this);
        return streamPlayers.some(({ waiting }) => waiting);
    }

    /**
     * @private
     * @param {Event} event
     */
    #handlePlayerSeeking(event) {
        if (this.waiting && !_firstPlay.get(this)) {
            // Still waiting for a stream to buffer - we should cancel seeking
            this.player.currentTime(_currentTimeWhenBufferingStarted.get(this));
        }
        else {
            this.syncAllPlayers().then(() => { });
        }
    }

    /**
     * @private
     * @param {Event} event
     */
    #handlePlayerTimeupdate(event) {
        if (!(this.player.paused() || this.waiting)) {
            const streamPlayers = _streamPlayers.get(this);
            const playerTime = this.player.currentTime();
            const playbackRate = this.player.playbackRate();

            for (const { streamPlayer } of streamPlayers) {
                streamPlayer.syncPlayback(playerTime, playbackRate);
            }
        }
    }

    /**
     * NOTE: DO NOT REMOVE! This method is used by the multistream middleware that is attached in the plugin constructor.
     * @private
     * @param {Event} event
     */
    handlePlayerPlaying(event) {
        if (this.waiting && !_firstPlay.get(this)) {
            // Still waiting for a stream to buffer - we should cancel playback
            this.player.pause();
            this.player.setTimeout(() => {
                this.player.tech(true).trigger('waiting');
            }, 0);
        }
        else {
            _firstPlay.set(this, false);
            const streamPlayers = _streamPlayers.get(this);
            for (const { streamPlayer } of streamPlayers) {
                streamPlayer.play();
            }
        }
    }

    /**
     * @private
     * @param {Event} event
     */
    #handlePlayerPause(event) {
        const streamPlayers = _streamPlayers.get(this);
        for (const { streamPlayer, waiting } of streamPlayers) {
            if (!waiting) {
                streamPlayer.pause();
            }
        }
    }

    /**
     * @private
     * @param {Event} event
     */
    #handlePlayerRatechange(event) {
        this.log('Playback rate changed for primary stream. Updating all secondary streams to match...');
        const streamPlayers = _streamPlayers.get(this);
        for (const { streamPlayer } of streamPlayers) {
            streamPlayer.playbackRate = this.player.playbackRate();
        }
    }

    dimensionsMatch(rect, region) {
        return rect.X === region.left && rect.Y === region.top && rect.Width === region.width && rect.Height === region.height;
    }

    /**
     * Cycles the positions of the stream players in the composition.
     */
    applyNewCompositionMap() {
        const composition = this.compositionList[this.currentCompositionIndex].VisualCompositions[0];
        const streams = this.getAllStreams();
        const smartZoom = this.player.smartzoom();
        const isZoomed = this.player.hasClass('mediasite-player--smartzoom-zoomed');
        //let zoomTargetSourceId = null;
        let nextRegionStreamId = null;

        //Cycle through all items in the composition and assign the visual item's size and position to the stream element
        composition.VisualItems.forEach((item, idx, comps) => {
            const layoutInfo = {
                left: item.TargetRect.X,
                top: item.TargetRect.Y,
                width: item.TargetRect.Width,
                height: item.TargetRect.Height
            };

            //Determine if the stream is the main stream or a secondary stream
            let streamId, streamElement;
            if (item.SourceId === this.primarySourceId) {
                streamId = 'multistream-main';
                streamElement = this.player.tech(true).el();
            } else {
                //Find the player stream matching the composition source
                const stream = streams.find(stream => stream.streamPlayer.stream.id === item.SourceId);
                //Some visual items may not be represented in a movable stream, like backgrounds
                if (!stream) {
                    return;
                }
                streamId = stream.id;
                streamElement = stream.streamPlayer.el();
            }

            //Move the smart zoom container and the stream element to new position with new dimensions
            smartZoom.moveRegion(streamId, layoutInfo);
            this.setStreamElementSize(streamElement, layoutInfo);

            if (isZoomed && streamId === smartZoom.lastFocused.dataset.id) {
                let zoomTargetIndex = idx + 1;
                while (!nextRegionStreamId) {
                    const nextVisCompItem = comps[zoomTargetIndex % comps.length];
                    nextRegionStreamId = nextVisCompItem.SourceId === this.primarySourceId ? 'multistream-main' :
                        streams.find(s => s.streamPlayer.stream.id === nextVisCompItem.SourceId)?.id;
                    if (zoomTargetIndex > comps.length) {
                        break;
                    }
                    zoomTargetIndex++;
                }
            }
        });

        if (nextRegionStreamId) {
            const nextRegion = smartZoom.lastFocused.parentNode.querySelector(`.mediasite-player__smartzoom-region[data-id="${nextRegionStreamId}"]`);
            smartZoom.unzoom(true);
            smartZoom.activeRegion = JSON.parse(nextRegion.dataset.region);
            const { left, top, width, height } = smartZoom.activeRegion;
            smartZoom.lastFocused = nextRegion;
            smartZoom.zoom(left, top, width, height);
        }
    }

    dispose() {
        const tech = this.player.tech(true);
        this.player.layout().addChild(tech, {}, 0);

        const element = tech.el();
        element.style.left = null;
        element.style.top = null;
        element.style.width = null;
        element.style.height = null;

        const container = _container.get(this);
        const streamPlayers = _streamPlayers.get(this);

        if (!streamPlayers) {
            super.dispose();
            return;
        }

        const hasSmartzoom = this.player?.usingPlugin('smartzoom');
        for (const { streamPlayer, id } of streamPlayers) {
            if (hasSmartzoom) {
                this.player.smartzoom().removeRegion(id);
            }
            
            container.removeChild(streamPlayer);
            streamPlayer.dispose();
        }

        if (hasSmartzoom) {
            this.player.smartzoom().removeRegion('multistream-main');
        }
        
        this.player.layout().removeChild(container);
        container.dispose();

        super.dispose();
    }
}

export { MultistreamPlugin };
